/**
 * @ai-content-hub/scorm — i18n
 *
 * Self-contained translations for SCORM Edit / Player / Uploader components.
 * Supports `en` and `vi`. The active locale is set globally via `setScormLocale()`
 * or per-component via the `language` prop on Edit components.
 */

export interface ScormTranslations {
    // Edit form labels
    titleLabel: string;
    titlePlaceholder: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    packageHeading12: string;
    packageHeading2004: string;
    packageUploaded: string;

    // Uploader
    uploadDropzone: string;
    uploadHint: string;
    uploadUploading: string;
    uploadSuccess: string;
    uploadReplace: string;
    uploadReplacePackage: string;
    uploadDelete: string;
    uploadError: string;
    uploadOnlyZip: string;
    /** EAA-1212: `{max}` is replaced with the configured cap in MB. */
    uploadTooLarge: string;
    uploadDeleteFailed: string;

    // Player
    playerNoPackage: string;
    playerNoSession: string;
    playerLoading: string;
    playerError: string;
    playerTitle: string;

    // Preview
    previewLoading: string;
    previewMode: string;

    // Manifest preview
    manifestTitle: string;
    manifestVersion: string;
    manifestOrganizations: string;
    manifestContentObjects: string;
    manifestLaunchUrl: string;

    // Submit confirm modal
    submitConfirmTitle: string;
    submitConfirmMessage: string;
    submitConfirmStatus: string;
    submitConfirmTime: string;
    submitConfirmScore: string;
    submitConfirmSubmit: string;
    submitConfirmCancel: string;
    submitConfirmSubmitted: string;
}

const en: ScormTranslations = {
    titleLabel: 'Title',
    titlePlaceholder: 'Enter title...',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter description...',
    packageHeading12: 'SCORM 1.2 Package',
    packageHeading2004: 'SCORM 2004 Package',
    packageUploaded: 'Package uploaded',

    uploadDropzone: 'Drop SCORM .zip file here or click to browse',
    uploadHint: 'Supports SCORM 1.2 and SCORM 2004',
    uploadUploading: 'Uploading SCORM package…',
    uploadSuccess: 'Upload successful',
    uploadReplace: 'Replace',
    uploadReplacePackage: 'Replace package',
    uploadDelete: 'Delete',
    uploadError: 'Upload failed',
    uploadOnlyZip: 'Only .zip files are allowed.',
    uploadTooLarge: 'The package is larger than the {max}MB limit.',
    uploadDeleteFailed: 'Failed to delete package.',

    playerNoPackage: 'No SCORM package has been uploaded yet. Please upload a SCORM file to get started.',
    playerNoSession: 'Session isn\'t available. Please launch this content from within a learning session.',
    playerLoading: 'Loading SCORM content…',
    playerError: 'Couldn\'t load the SCORM content. Please refresh the page or contact support.',
    playerTitle: 'SCORM Content',

    previewLoading: 'Loading SCORM preview…',
    previewMode: 'Preview mode — tracking is disabled.',

    manifestTitle: 'Title',
    manifestVersion: 'Version',
    manifestOrganizations: 'Organizations',
    manifestContentObjects: 'Content Objects',
    manifestLaunchUrl: 'Launch URL:',

    submitConfirmTitle: 'Submit SCORM Results',
    submitConfirmMessage: 'The learner has completed the activity. Do you want to submit these results?',
    submitConfirmStatus: 'Status',
    submitConfirmTime: 'Time (ms)',
    submitConfirmScore: 'Score',
    submitConfirmSubmit: 'Submit',
    submitConfirmCancel: 'Cancel',
    submitConfirmSubmitted: 'Results submitted',
};

const vi: ScormTranslations = {
    titleLabel: 'Tiêu đề',
    titlePlaceholder: 'Nhập tiêu đề...',
    descriptionLabel: 'Mô tả',
    descriptionPlaceholder: 'Nhập mô tả...',
    packageHeading12: 'Gói SCORM 1.2',
    packageHeading2004: 'Gói SCORM 2004',
    packageUploaded: 'Đã tải lên gói',

    uploadDropzone: 'Kéo thả tệp .zip SCORM vào đây hoặc nhấn để chọn',
    uploadHint: 'Hỗ trợ SCORM 1.2 và SCORM 2004',
    uploadUploading: 'Đang tải lên gói SCORM…',
    uploadSuccess: 'Tải lên thành công',
    uploadReplace: 'Thay thế',
    uploadReplacePackage: 'Thay thế gói',
    uploadDelete: 'Xóa',
    uploadError: 'Tải lên thất bại',
    uploadOnlyZip: 'Chỉ chấp nhận tệp .zip.',
    uploadTooLarge: 'Gói vượt quá giới hạn {max}MB.',
    uploadDeleteFailed: 'Không thể xóa gói.',

    playerNoPackage: 'Chưa tải lên gói SCORM. Vui lòng tải lên tệp SCORM để bắt đầu.',
    playerNoSession: 'Phiên không khả dụng. Vui lòng khởi chạy nội dung này từ trong một phiên học tập.',
    playerLoading: 'Đang tải nội dung SCORM…',
    playerError: 'Không thể tải nội dung SCORM. Vui lòng tải lại trang hoặc liên hệ hỗ trợ.',
    playerTitle: 'Nội dung SCORM',

    previewLoading: 'Đang tải xem trước SCORM…',
    previewMode: 'Chế độ xem trước — theo dõi bị tắt.',

    manifestTitle: 'Tiêu đề',
    manifestVersion: 'Phiên bản',
    manifestOrganizations: 'Tổ chức',
    manifestContentObjects: 'Đối tượng nội dung',
    manifestLaunchUrl: 'URL khởi chạy:',

    submitConfirmTitle: 'Nộp kết quả SCORM',
    submitConfirmMessage: 'Người học đã hoàn thành hoạt động. Bạn có muốn nộp kết quả này không?',
    submitConfirmStatus: 'Trạng thái',
    submitConfirmTime: 'Thời gian (ms)',
    submitConfirmScore: 'Điểm',
    submitConfirmSubmit: 'Nộp',
    submitConfirmCancel: 'Hủy',
    submitConfirmSubmitted: 'Đã nộp kết quả',
};

const translations: Record<string, ScormTranslations> = { en, vi };

let currentLocale = 'en';

/** Set the global locale for all SCORM components. */
export function setScormLocale(locale: string): void {
    const normalized = locale?.toLowerCase().startsWith('vi') ? 'vi' : 'en';
    currentLocale = normalized;
}

/** Get the current global locale. */
export function getScormLocale(): string {
    return currentLocale;
}

/** Get translations for the given locale (or current global locale). */
export function getScormTranslations(locale?: string): ScormTranslations {
    if (locale) {
        const normalized = locale.toLowerCase().startsWith('vi') ? 'vi' : 'en';
        return translations[normalized] || translations.en;
    }
    return translations[currentLocale] || translations.en;
}

/** React hook — returns translations for the current or given locale. */
export function useScormTranslations(locale?: string): ScormTranslations {
    return getScormTranslations(locale);
}
